// hooks/useCompanyPage.js
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const formatAddedTime = (addedtime) => {
    if (!addedtime) return "Invalid date";

    const now = new Date();
    const addedDate = new Date(addedtime);
    const diffTime = Math.abs(now.getTime() - addedDate.getTime());
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMinutes < 1) return "Added just now";
    if (diffMinutes === 1) return "Added 1 minute ago";
    if (diffMinutes < 60) return `Added ${diffMinutes} minutes ago`;
    if (diffHours === 1) return "Added 1 hour ago";
    if (diffHours < 24) return `Added ${diffHours} hours ago`;
    if (diffDays === 1) return "Added 1 day ago";
    if (diffDays < 7) return `Added ${diffDays} days ago`;
    if (diffWeeks === 1) return "Added 1 week ago";
    if (diffWeeks < 4) return `Added ${diffWeeks} weeks ago`;
    if (diffDays < 365) {
        return `Added ${addedDate.getDate()} ${addedDate.toLocaleString("default", { month: "short" })}`;
    }
    return `Added ${addedDate.getDate()} ${addedDate.toLocaleString("default", { month: "short" })} ${addedDate.getFullYear()}`;
};

export function useCompanyPage(companySlug) {
    const navigate = useNavigate();
    const { handleError } = useQueryError();

    const { data, isLoading, error } = useQuery({
        queryKey: queryKeys.company.bySlug(companySlug),
        queryFn: async () => {
            try {
                // Convertir el slug a nombre de empresa
                const companyName = companySlug
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                // Buscar la empresa por nombre
                const { data: companyData, error: companyError } = await supabase
                    .from("cards")
                    .select(`
                        *, 
                        categories(name), 
                        company_overview(
                            button_color, 
                            companyname, 
                            description, 
                            keyfeature1_title, 
                            keyfeature1_description, 
                            keyfeature2_title, 
                            keyfeature2_description, 
                            keyfeature3_title, 
                            keyfeature3_description, 
                            keyfeature4_title, 
                            keyfeature4_description, 
                            keyfeature5_title, 
                            keyfeature5_description, 
                            videopreview, 
                            videopreview_phone, 
                            companylink
                        )
                    `)
                    .ilike('companyname', companyName)
                    .single();

                if (companyError || !companyData) {
                    throw new Error('Company not found');
                }

                return {
                    ...companyData,
                    company_overview: {
                        ...companyData.company_overview,
                        button_color: companyData.company_overview.button_color || "#7443E8"
                    },
                    formattedAddedTime: formatAddedTime(companyData.addedtime)
                };
            } catch (error) {
                console.error("Error fetching company:", error);
                throw error;
            }
        },
        onError: (error) => {
            handleError(error);
            navigate('/404');
        },
        staleTime: 1000 * 60 * 5, // 5 minutos
        cacheTime: 1000 * 60 * 30, // 30 minutos
        refetchOnWindowFocus: false
    });

    return {
        companyData: data,
        isLoading,
        error
    };
}